import { useMemo } from 'react';
import { useApi } from '../infrastructure/useApi';
import { DEFAULT_API_OPTIONS } from '../infrastructure/defaults';
import { API_URLS } from '../apiUrls';
import { API_METHODS } from '../constants/apiMethods';

/** @typedef {import('../models/orderList').OrdersListPayload} OrdersListPayload */

/**
 * @typedef {Object} GetOrderListParams
 * @property {number} limit
 * @property {number} page
 * @property {string} sort
 * @property {Record<string, any>} [filters]
 */

/**
 * @typedef {Object} GetOrderListResult
 * @property {OrdersListPayload} [data]
 * @property {boolean} [isAborted]
 * @property {boolean} [isError]
 * @property {any} [error]
 */

/**
 * Hook for accessing Orders API methods.
 *
 * Usage:
 * const { getOrderList, getDetails, cancel } = useOrdersApi();
 * await getOrderList(params);
 *
 * Cancellation scope:
 * - cancel() applies to requests started by this hook instance.
 * - For independent cancellation scopes, create separate hook instances.
 */
export const useOrdersApi = (baseOptions = DEFAULT_API_OPTIONS) => {
	const { call, cancel } = useApi(baseOptions);

	return useMemo(
		() => ({
			/**
			 * @param {GetOrderListParams} params
			 * @returns {Promise<GetOrderListResult>}
			 */
			getOrderList: (params) =>
				call({
					url: API_URLS.order.getOrderList(),
					method: API_METHODS.POST,
					payload: {
						Page_Size: params.limit,
						Page_Number: params.page + 1,
						Order_By: params.sort,
						Status: params.filters?.status
							? Object.keys(params.filters.status)
									.filter((key) => params.filters.status[key])
									.toString()
							: undefined,
						Filters:
							params.filters?.shipToLocation ||
							params.filters?.siteLocation ||
							params.filters?.orderDate?.startDate ||
							params.filters?.orderDate?.endDate ||
							params.filters?.dueDate?.startDate ||
							params.filters?.dueDate?.endDate
								? {
										Ship_To_Number: params.filters?.shipToLocation,
										Plant_Code: params.filters?.siteLocation,
										Order_Date:
											params.filters?.orderDate?.startDate ||
											params.filters?.orderDate?.endDate
												? {
														Begin_Date: params.filters?.orderDate?.startDate,
														End_Date: params.filters?.orderDate?.endDate,
												  }
												: undefined,
										Order_Due_Date:
											params.filters?.dueDate?.startDate ||
											params.filters?.dueDate?.endDate
												? {
														Begin_Date: params.filters?.dueDate?.startDate,
														End_Date: params.filters?.dueDate?.endDate,
												  }
												: undefined,
								  }
								: undefined,
					},
				}),

			getDetails: ({ id, source }) =>
				call({
					url: API_URLS.order.getOrderDetail({ orderNumber: id, source }),
					method: API_METHODS.GET,
				}),

			downloadOrderList: (format, payload) =>
				call({
					url: API_URLS.order.downloadOrderList(format),
					method: API_METHODS.POST_BLOB,
					payload,
				}),

			cancel,
		}),
		[call, cancel]
	);
};
